<template>
  <img
    v-if="fileTypeSupported"
    ref="imageRef"
    v-bind="$attrs"
    :src="`${bunnySrcUrl}?width=768&optimizer=image`"
    :alt="alt ?? src?.alt"
    :loading="loading"
    :sizes="sizesAttribute"
    :srcset="srcSet"
    :style="`object-position: ${src?.focus_css};`"
  />

  <img
    v-else-if="props.src"
    v-bind="$attrs"
    ref="imageRef"
    :alt="src?.alt"
    :src="props.src?.permalink || props.src"
    :loading="loading"
    :style="`object-position: ${src?.focus_css};`"
  />
</template>

<!-- eslint-disable-next-line vue/block-lang -->
<script setup>
const props = defineProps({
  src: {
    type: [String, Object],
    default: () => ({}),
  },
  alt: {
    type: String,
    default: null,
  },
  width: {
    type: Number,
    default: null,
  },
  height: {
    type: Number,
    default: null,
  },
  loading: {
    type: String,
    default: 'lazy',
  },
})

const imageRef = ref(null)
const imageWidth = ref(null)
const imageWidthToWindowWidthRatio = ref(null)
const runtimeConfig = useRuntimeConfig()
const bunnyBaseUrl = runtimeConfig.public.BUNNY_BASE_URL

const screens = {
  'xs': '320px',
  'sm': '640px',
  'md': '768px',
  'lg': '1024px',
  'xl': '1280px',
  '2xl': '1600px',
  '3xl': '2000px',
  '4xl': '2560px',
  '5xl': '3840px',
}

// COMPUTED
const sizesAttribute = computed(() => {
  if (imageWidthToWindowWidthRatio.value === null) {
    return '1px'
  }
  const imageWidthPercentage = Math.round(
    imageWidthToWindowWidthRatio.value * 100,
  )
  return `${imageWidthPercentage}vw`
})

const srcSet = computed(() => {
  return Object.entries(screens)
    .map(([key, screenWidth]) => {
      const width = parseInt(screenWidth.replace('px', ''))
      const path = getBunnySrcUrlWithParams(width, 'auto')
      return `${path} ${width}w`
    })
    .join(', ')
})

const srcRelativeUrl = computed(() => {
  const url = props.src?.permalink || props.src

  if (!url) return null

  // Create a URL object from the given URL string
  const urlObject = new URL(url)

  // Return the pathname part of the URL, which is the relative path
  return urlObject.pathname
})

const bunnySrcUrl = computed(() => {
  const rawUrl = `${bunnyBaseUrl}/${srcRelativeUrl.value}`
  // Remove multiple slashes, but preserve the initial `https://`
  // this ensures that it doesn't mater if the bunnyBaseUrl is set with or without a trailing slash
  return rawUrl.replace(/([^:]\/)\/+/g, '$1')
})

// FUNCTIONS
function getBunnySrcUrlWithParams(width, height) {
  return `${bunnySrcUrl.value}?width=${width}&height=${height}&optimizer=image`
}

const updateImageWidth = useDebounceFn(() => {
  if (imageRef.value) {
    imageWidth.value = imageRef.value.clientWidth
    imageWidthToWindowWidthRatio.value = imageWidth.value / window.innerWidth
  }
}, 1000)

const fileTypeSupported = computed(() => {
  if (!props.src) {
    return false
  }

  const allowedExtensions = ['jpg', 'png', 'gif', 'webp', 'jpeg', 'tiff']

  // Check if the src is a string and determine file extension
  if (props.src && typeof props.src === 'string') {
    // Split the URL at the query string delimiter (?), take the first part (path)
    const pathWithoutQuery = props.src.split('?')[0]
    const regex = /(?:\.([^.]+))?$/
    const fileExtensionMatch = regex.exec(pathWithoutQuery)

    // Extract the extension (if it exists)
    const fileExtension = fileExtensionMatch && fileExtensionMatch[1]

    return (
      fileExtension && allowedExtensions.includes(fileExtension.toLowerCase())
    )
  }

  // Check if the src is an object and has an extension property
  if (typeof props.src === 'object' && props.src.extension) {
    return allowedExtensions.includes(props.src.extension.toLowerCase())
  }

  return false
})

onMounted(async () => {
  updateImageWidth()
  window.addEventListener('resize', updateImageWidth)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateImageWidth)
})
</script>
